import { useEffect, useRef, type ReactNode } from 'react';
import { COLORS } from '../../constants/theme';

interface AccordionCardProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  /** Used to derive a stable `id`/`aria-controls` pair for the trigger/panel. */
  panelId: string;
  children: ReactNode;
}

/**
 * SF-164 (v1.3 fix, Critical Issue #3): bespoke accordion card for the Flow Breakdown sections.
 * `GlanceCard`'s header renders internally with only a small `headerAction` corner slot — it
 * cannot host a full-header tap target, so this is a new, separate component rather than a
 * widened `GlanceCard` API for one caller. Styled to match `GlanceCard`'s visual chrome
 * (`rounded-lg`, `bg-game-800`, `border border-game-700`) but with a real full-width
 * `<button aria-expanded aria-controls>` wrapping its title.
 *
 * Content is DOM-absent when collapsed (`{isOpen && <panel/>}`), not CSS-hidden — this project's
 * established convention for reliable test queries (CLAUDE.md).
 *
 * Scroll behavior (v1.3 addition, ux-designer C9): expanding a panel scrolls its trigger into
 * view, offsetting the scroll-jump the default-expanded-Gold panel otherwise causes on toggle.
 */
export function AccordionCard({ title, isOpen, onToggle, panelId, children }: AccordionCardProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      buttonRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen]);

  return (
    <div className="rounded-lg overflow-hidden bg-game-800 border border-game-700">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={`${panelId}-panel`}
        id={`${panelId}-trigger`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
        style={{ minHeight: 44 }}
      >
        <h3 className="text-sm font-semibold text-game-50">{title}</h3>
        <span
          aria-hidden="true"
          style={{
            color: COLORS.text.tertiary,
            display: 'inline-block',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div
          id={`${panelId}-panel`}
          role="region"
          aria-labelledby={`${panelId}-trigger`}
          className="px-4 pb-3 flex flex-col gap-2"
        >
          {children}
        </div>
      )}
    </div>
  );
}
